import React, { useState, useEffect, useRef } from 'react';
import { AudioPlayerProps, AudioVersion } from '../../types';
import { formatTime } from '../../utils/timeFormatter';
import VersionSelector from '../VersionSelector';
import './AudioPlayer.css';

const AudioPlayer: React.FC<AudioPlayerProps> = ({ seriousAudioUrl, slangAudioUrl, title, availableTracks = [], onTrackSelect, currentTrackId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentVersion, setCurrentVersion] = useState<AudioVersion>(
    seriousAudioUrl ? 'serious' : 'discussion'
  );
  const audioRef = useRef<HTMLAudioElement>(null);

  const currentAudioUrl = currentVersion === 'serious' ? seriousAudioUrl : slangAudioUrl;
  const audioBasePath = '/audios/';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Handle version change - reset and pause
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Pause current playback
    audio.pause();
    setIsPlaying(false);
    setCurrentTime(0);
    
    // Load new audio
    audio.load();
  }, [currentVersion]);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio || !currentAudioUrl) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => console.error('Erreur de lecture:', err));
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVersionChange = (version: AudioVersion) => {
    setCurrentVersion(version);
  };

  const handlePlayerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!seriousAudioUrl && !slangAudioUrl) return null;

  return (
    <div className="audio-player-container" onClick={handlePlayerClick}>
      <div className="audio-player-header">
        {availableTracks.length > 1 && onTrackSelect ? (
          <select 
            className="audio-player-track-select"
            value={currentTrackId || ''}
            onChange={(e) => onTrackSelect(e.target.value)}
          >
            {availableTracks.map(track => (
              <option key={track.id} value={track.id}>{track.title}</option>
            ))}
          </select>
        ) : (
          <h3 className="audio-player-title">{title}</h3>
        )}
      </div>
      
      <VersionSelector
        hasSeriousVersion={!!seriousAudioUrl}
        hasDiscussionVersion={!!slangAudioUrl}
        currentVersion={currentVersion}
        onVersionChange={handleVersionChange}
      />
      
      <div className="audio-player">
        <audio ref={audioRef} src={currentAudioUrl ? audioBasePath + currentAudioUrl : undefined} preload="metadata" />
        
        <div className="audio-controls">
          <button 
            className={`control-btn play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={togglePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            disabled={!currentAudioUrl}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1"></rect>
                <rect x="14" y="4" width="4" height="16" rx="1"></rect>
              </svg>
            ) : (
              <svg className="icon-play" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5.14V18.86L19 12L8 5.14Z" />
              </svg>
            )}
          </button>
        </div>

        <div className="progress-container">
          <span className="time-display">{formatTime(currentTime)}</span>
          <input
            type="range"
            className="progress-bar"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleProgressChange}
            disabled={!currentAudioUrl}
            style={{
              background: duration > 0 
                ? `linear-gradient(to right, #007AFF 0%, #007AFF ${(currentTime / duration) * 100}%, #d1d1d6 ${(currentTime / duration) * 100}%, #d1d1d6 100%)`
                : '#d1d1d6'
            }}
          />
          <span className="time-display">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
